import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-map-gl';
import type mapboxgl from 'mapbox-gl';
import { isColorPickerOpen } from 'components/color-picker';
import type { PickedWell, SectionMode, SectionVertex } from './types';

/**
 * How far the pointer may travel between press and release and still count as a click.
 * Looser than Mapbox's own 3px, which is tight enough that ordinary hand movement turns
 * a deliberate click into nothing. A pan travels much further, so the two stay distinct.
 */
export const CLICK_SLOP_PX = 8;

/**
 * How far from the pointer a well may sit and still be the one picked. Aiming at a
 * four-pixel dot is not a reasonable thing to ask, so the target is sized to the
 * pointer instead.
 */
const WELL_SNAP_PX = 18;

/**
 * The one hole direction that can be a section vertex, matching the backend's gate: a
 * vertical occupies one surface location, so it stands on the line unambiguously. The
 * map draws DIRECTIONAL and PINNATE in the same layers, so the hit must be filtered.
 */
const ELIGIBLE_HOLE_DIRECTION = 'VERTICAL';

/** Mapbox reports the features under the pointer on layer-scoped listeners. */
type MapMouseEventWithFeatures = mapboxgl.MapMouseEvent & {
  features?: mapboxgl.MapboxGeoJSONFeature[];
};

export interface CrossSectionDrawingOptions {
  /** The tool is armed and a mode has been chosen, so a click means something. */
  enabled: boolean;
  /** Layers drawing wells that could be picked, hit-tested in Well to Well. */
  wellLayerIds?: string[];
  /** A well was clicked: picked if it was not on the section, dropped if it was. */
  onToggleWell?: (well: PickedWell) => void;
  /** Which mode is drawing; only Arbitrary Line takes map clicks as vertices. */
  mode: SectionMode | null;
  /** The committed vertices, in click order. */
  vertices: SectionVertex[];
  /** The layer painting the vertices; its features must carry `index`. */
  vertexLayerId: string;
  /** A click on open map appends a vertex here. */
  onAddVertex: (vertex: SectionVertex) => void;
  /** A dragged vertex landed. Fires on release, so one drag is one query. */
  onMoveVertex: (index: number, vertex: SectionVertex) => void;
}

export interface CrossSectionDrawingState {
  /**
   * The line as it looks mid-drag, or null when nothing is being dragged. The map
   * follows the cursor from here while the section itself keeps showing the last
   * landed geometry.
   */
  previewVertices: SectionVertex[] | null;
}

/**
 * A well as the map names it: lease name and well number, the same composition the
 * well labels use.
 *
 * @param {mapboxgl.MapboxGeoJSONFeature} feature - The well tile feature.
 * @return {string | null} The name, or null where the tile carries neither part.
 */
const wellName = (feature: mapboxgl.MapboxGeoJSONFeature): string | null => {
  const parts = [
    feature.properties?.lease_name,
    feature.properties?.well_number,
  ].filter((part): part is string => typeof part === 'string' && part !== '');
  return parts.length > 0 ? parts.join(' ') : null;
};

/**
 * Clears the cursor a gesture set, from an effect cleanup. A host that unmounts its map
 * with the tool armed (Area Details hiding its Map section) has the map removed before
 * these cleanups run, and a removed map has no canvas left to reset.
 *
 * @param {mapboxgl.Map} map - The map, possibly already removed.
 */
const clearCursor = (map: mapboxgl.Map): void => {
  const canvas = map.getCanvas() as HTMLCanvasElement | undefined;
  if (canvas) {
    canvas.style.cursor = '';
  }
};

/**
 * The eligible well nearest the pointer, or null when the click was a miss.
 *
 * Hit-tested against a box around the pointer rather than the pointer itself, then
 * resolved to the nearest candidate: aiming at a four-pixel dot is not a reasonable
 * thing to ask, and with overlapping boxes the pick would otherwise fall to layer
 * order rather than to the well that was aimed at.
 *
 * @param {mapboxgl.Map} map - The map.
 * @param {MapLayerMouseEvent} event - The click.
 * @param {Array} layerIds - Layers drawing wells that can be picked.
 * @return {PickedWell | null} The well to toggle, or null.
 */
const nearestEligibleWell = (
  map: mapboxgl.Map,
  event: mapboxgl.MapMouseEvent,
  layerIds: string[]
): PickedWell | null => {
  const present = layerIds.filter((layerId) => map.getLayer(layerId));
  if (present.length === 0) {
    return null;
  }
  const box: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [event.point.x - WELL_SNAP_PX, event.point.y - WELL_SNAP_PX],
    [event.point.x + WELL_SNAP_PX, event.point.y + WELL_SNAP_PX],
  ];

  let nearest: PickedWell | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  map.queryRenderedFeatures(box, { layers: present }).forEach((feature) => {
    const uwi = feature.properties?.uwi;
    if (
      typeof uwi !== 'string' ||
      feature.properties?.hole_direction !== ELIGIBLE_HOLE_DIRECTION ||
      feature.geometry.type !== 'Point'
    ) {
      return;
    }
    const [lon, lat] = feature.geometry.coordinates;
    const at = map.project([lon, lat]);
    const distance = Math.hypot(at.x - event.point.x, at.y - event.point.y);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = { uwi, lat, lon, name: wellName(feature) };
    }
  });

  return nearest;
};

/**
 * Drawing and editing the Arbitrary Line section on the map.
 *
 * Three details are the reason this is a hook rather than inline handlers:
 *
 * - **Placement runs off mousedown/mouseup, not Mapbox's `click`.** Mapbox
 *   suppresses `click` entirely once the pointer travels its own 3px tolerance
 *   between press and release, and while drawing a section the hand is usually
 *   still moving as the button goes down — so points, the first one especially,
 *   were being dropped silently.
 * - **A vertex press is remembered across effects.** Grabbing a vertex is not
 *   placing one, and the flag has to outlive a press whose release never arrived
 *   (the pointer came up off the canvas, or over the drawer) or it would be spent
 *   swallowing the first real click of the next section.
 * - **The section is only told once the vertex lands.** Every intermediate
 *   position would otherwise re-sample every grid along a new line, tens of times
 *   a second, for geometry the user has not settled on.
 *
 * @param {CrossSectionDrawingOptions} options - The line, its layer, and callbacks.
 * @return {CrossSectionDrawingState} The mid-drag preview, when there is one.
 */
export const useCrossSectionDrawing = ({
  enabled,
  mode,
  vertices,
  vertexLayerId,
  wellLayerIds = [],
  onAddVertex,
  onMoveVertex,
  onToggleWell,
}: CrossSectionDrawingOptions): CrossSectionDrawingState => {
  const { current: mapRef } = useMap();
  const [previewVertices, setPreviewVertices] = useState<
    SectionVertex[] | null
  >(null);

  // Read inside map handlers, which are registered once rather than on every
  // vertex change — re-registering mid-drag would lose the drag.
  const verticesRef = useRef(vertices);
  verticesRef.current = vertices;

  // Shared by both effects below: set while a vertex is being grabbed, so the
  // release that ends the drag is not also read as placing a point.
  const vertexPressRef = useRef(false);

  // The drag lives in refs rather than in the effect that registers the handlers: the
  // preview re-renders on every mousemove, and anything re-registering mid-gesture would
  // drop the drag halfway and leave the vertex where it was.
  const dragIndexRef = useRef<number | null>(null);
  const draggedToRef = useRef<SectionVertex | null>(null);

  // Arbitrary Line takes map clicks as vertices; Well to Well takes them as wells.
  // Both are "the map is live", which is what the cursor and the slop are about.
  const drawing = enabled && mode !== null;
  const drawingLine = enabled && mode === 'arbitrary_line';

  // Placement.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !drawing) {
      return undefined;
    }
    map.getCanvas().style.cursor = 'crosshair';

    // Arming is the moment to forget any unfinished vertex press: see the note
    // above on why the flag outlives the gesture that set it.
    vertexPressRef.current = false;

    let pressAt: { x: number; y: number } | null = null;
    // A press that closed a floating popover is that popover's dismissal, not a point.
    let dismissing = false;

    // Left button only. A right drag rotates the map, and `click` used to filter
    // that out implicitly.
    const handlePress = (event: mapboxgl.MapMouseEvent) => {
      // A colour picker's popover floats over the map and closes on mousedown, so the
      // press that dismisses it lands here too — placing a point would edit the section
      // on a gesture meant to dismiss. Read at press: by release the popover has closed.
      dismissing = isColorPickerOpen();
      pressAt =
        event.originalEvent.button === 0
          ? { x: event.point.x, y: event.point.y }
          : null;
    };

    const handleRelease = (event: mapboxgl.MapMouseEvent) => {
      const from = pressAt;
      pressAt = null;
      if (dismissing) {
        dismissing = false;
        return;
      }
      // Cleared before the slop test, since a drag releases far from its press
      // and would otherwise leave the flag set for the next real click.
      if (vertexPressRef.current) {
        vertexPressRef.current = false;
        return;
      }
      if (!from) {
        return;
      }
      const travel = Math.hypot(event.point.x - from.x, event.point.y - from.y);
      if (travel > CLICK_SLOP_PX) {
        return;
      }

      if (mode === 'well_to_well') {
        // Only wells define the section here, so a click on open map is not a point —
        // it is a miss.
        const well = nearestEligibleWell(map, event, wellLayerIds);
        if (well) {
          onToggleWell?.(well);
        }
        return;
      }

      onAddVertex({ lat: event.lngLat.lat, lon: event.lngLat.lng });
    };

    map.on('mousedown', handlePress);
    map.on('mouseup', handleRelease);

    return () => {
      map.off('mousedown', handlePress);
      map.off('mouseup', handleRelease);
      clearCursor(map);
    };
  }, [mapRef, drawing, mode, onAddVertex, onToggleWell, wellLayerIds]);

  // Dragging a placed vertex. Arbitrary Line only — in Well to Well a vertex is a
  // well, and dragging one would claim the well had moved.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !drawingLine) {
      return undefined;
    }

    const handleDown = (event: MapMouseEventWithFeatures) => {
      const index = event.features?.[0]?.properties?.index;
      if (typeof index !== 'number') {
        return;
      }
      // Stop the map panning out from under the vertex being dragged.
      event.preventDefault();
      dragIndexRef.current = index;
      draggedToRef.current = null;
      vertexPressRef.current = true;
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
    };

    const handleMove = (event: mapboxgl.MapMouseEvent) => {
      const dragIndex = dragIndexRef.current;
      if (dragIndex === null) {
        return;
      }
      const at: SectionVertex = {
        lat: event.lngLat.lat,
        lon: event.lngLat.lng,
      };
      draggedToRef.current = at;
      setPreviewVertices(
        verticesRef.current.map((vertex, index) => {
          return index === dragIndex ? at : vertex;
        })
      );
    };

    const endDrag = () => {
      const index = dragIndexRef.current;
      if (index === null) {
        return;
      }
      const landed = draggedToRef.current;
      dragIndexRef.current = null;
      draggedToRef.current = null;
      map.dragPan.enable();
      map.getCanvas().style.cursor = 'crosshair';
      // Dropping the preview and committing in one go: the section rebuilds from
      // the new geometry, and the map goes back to reading it.
      setPreviewVertices(null);
      if (landed) {
        onMoveVertex(index, landed);
      }
    };

    const handleEnter = () => {
      if (dragIndexRef.current === null) {
        map.getCanvas().style.cursor = 'grab';
      }
    };
    const handleLeave = () => {
      if (dragIndexRef.current === null) {
        map.getCanvas().style.cursor = 'crosshair';
      }
    };

    // Mapbox only reports a release landing on its own canvas, and a vertex near the
    // bottom is dragged towards the drawer — leaving the drag open and its press flagged,
    // swallowing the next click. The window sees every release; endDrag no-ops otherwise.
    const handleWindowUp = () => {
      endDrag();
      vertexPressRef.current = false;
    };

    map.on('mousedown', vertexLayerId, handleDown);
    map.on('mouseenter', vertexLayerId, handleEnter);
    map.on('mouseleave', vertexLayerId, handleLeave);
    map.on('mousemove', handleMove);
    map.on('mouseup', endDrag);
    // Bubble phase, so the canvas's own mouseup — and the placement riding on it —
    // has already read the flag before this clears it.
    window.addEventListener('mouseup', handleWindowUp);

    return () => {
      map.off('mousedown', vertexLayerId, handleDown);
      map.off('mouseenter', vertexLayerId, handleEnter);
      map.off('mouseleave', vertexLayerId, handleLeave);
      map.off('mousemove', handleMove);
      map.off('mouseup', endDrag);
      window.removeEventListener('mouseup', handleWindowUp);
      map.dragPan.enable();
      // Cleared, not restored. The placement effect owns the cursor while the map is live
      // and re-applies the crosshair right after, so a mode switch keeps it while a close
      // does not. Restoring here left the crosshair behind, this cleanup running last.
      clearCursor(map);
    };
  }, [mapRef, drawingLine, vertexLayerId, onMoveVertex]);

  // A section cleared or a mode switched mid-drag leaves a preview, and a held drag
  // index, answering a line that no longer exists. Keyed on line drawing rather than
  // on the map being live: switching to Well to Well ends the drag too.
  useEffect(() => {
    if (!drawingLine) {
      dragIndexRef.current = null;
      draggedToRef.current = null;
      vertexPressRef.current = false;
      setPreviewVertices(null);
    }
  }, [drawingLine]);

  return { previewVertices };
};
